/* eslint-disable no-restricted-syntax */
/* eslint-disable no-plusplus */
import { call, put, putResolve, select } from 'redux-saga/effects';
import { getPricingLineItem, updateProductInfo } from '../../actions/pricingActions';
import {
  configSelector,
  configuratorSelector,
  orderSummarySelector,
  productDetailsAttributeSelector,
  currentSelectedItemSelector,
  previousmultipleLineIemSelector,
  maxColorSelector,
  productDetailsSelector,
} from '../../selectors';
import { customLineItemPricing } from '../../api/skuMapperApi';
import { getPriceData, getSelectedChoices } from './_utils';
import {
  updateMultipleItem,
  currentSelectedItems,
} from '../../actions/multipleLineItem/multipleLinePricing';
import { setLineItemPrice } from '../../actions/orderSummaryActions';
import { getSku } from '../../actions/getSkuMap';
import getThdProductDetailsOrNotItsCool from './_getThdProductDetailsOrNotItsCool';

function* updatingMultipleLine(gasFee = []) {
  const resp = yield call(customLineItemPricing, gasFee);
  return resp;
}

/**
 * returns mergeData of two arrays
 * @param {Array} payload the payload from local data
 * @param {Array} response the saga effect with API response
 * @return {Array} mergedData of payload and response after orchestration
 *  for future usabilty in UI based on redux store updates
 * */
function handleMerge(payload, response) {
  if (!response?.length) return [];
  const obj1 = {};
  const obj2 = {};
  for (const gasFee of payload) obj1[gasFee.uniqueIdentifier] = gasFee;
  for (const resp of response) obj2[resp.uniqueIdentifier] = resp;

  for (const id in obj1) {
    if (id in obj2) obj2[id] = { ...obj2[id], ...obj1[id] };
  }
  const respVal = Object.values(obj2).map((value) =>
    Object.fromEntries(Object.entries(value).map(([k, v]) => [k.toLowerCase(), v]))
  );
  const mergeData = Object.values(Object.fromEntries(new Map(Object.entries(respVal))));

  return mergeData;
}

function getLogoLocation(uploadImages, ppeProductType) {

  if (ppeProductType !== 'vest') return 0;

  let logoLocation = 0;
  const hasFrontLogo = uploadImages.some(
    (img) => img.uploadImageTabName === 'Front' && img.uploadedImageUrl !== null
  );
  const hasBackLogo = uploadImages.some(
    (img) => img.uploadImageTabName === 'Back' && img.uploadedImageUrl !== null
  );

  if (hasFrontLogo && hasBackLogo) {
    logoLocation = 3;
  } else if (hasFrontLogo) {
    logoLocation = 1;
  } else if (hasBackLogo) {
    logoLocation = 2;
  }
  return logoLocation;
}
function* getLineItemPricing(skuMap, storeNumber) {
  const { productName } = yield select(configSelector);
  const optionsChoices = {};
  const { options, choices } = yield select(configuratorSelector);
  const optionchoices = yield getSelectedChoices(options, choices);
  const { currentItemQuantity } = yield select(orderSummarySelector);
  const { sizeOfUnit } = yield select(productDetailsAttributeSelector) || '';

  const { previousSelections } = yield select(previousmultipleLineIemSelector) || [];

  const { currentSelectedItem } = yield select(currentSelectedItemSelector);

  const { uniqueIdentifier } = currentSelectedItem[0];
  const unitOfProduct = parseInt(sizeOfUnit, 10) || 1;

  for (let i = 0; i < optionchoices.length; i++) {
    const { optionId, shortLabel } = optionchoices[i];
    const customShortLabel = options[optionId].shortLabel.split(' ').join('').toLowerCase();
    optionsChoices[customShortLabel] = shortLabel;
  }

  const {
    uploadImages: { uploadImages },
    config,
  } = yield select();

  const { skus } = yield putResolve(getSku());
  let logoCount = uploadImages.filter((img) => !!img.uploadedImageFileName).length;
  logoCount = logoCount > 0 ? logoCount : 1;

  const logoMax = yield select(maxColorSelector);
  const { siteId, productId, attributes } = yield select(productDetailsSelector);

  const logoLocation = getLogoLocation(uploadImages, attributes?.ppeProductType);
  const currentSelection = [
    {
      ...currentSelectedItem[0],
      productName,
      partNumber: skus[0].sku,
      quantity: currentItemQuantity || 1,
      unit: unitOfProduct,
      logos: logoCount,
      logoColors: logoMax,
      logoLocation
    },
  ];

  const allPayloadData = [...previousSelections, ...currentSelection];

  const ppePricePayload = {
    selectedChoices: allPayloadData,
    productId,
    siteId,
    returnCost : false
  };
  if (ppePricePayload && ppePricePayload.selectedChoices.length > 0) {
    yield put(currentSelectedItems(currentSelection));
    const resp = yield call(updatingMultipleLine, ppePricePayload);
    const mergedData = handleMerge(allPayloadData, resp);

    yield put(updateMultipleItem(mergedData));
    const { retail, quantity } = resp.filter(
      (item) => item.uniqueIdentifier === uniqueIdentifier
    )[0];
    yield put(getPricingLineItem(retail));
    const { partnerProductId } = yield select(configSelector);

    const thdDetails = yield call(
      getThdProductDetailsOrNotItsCool,
      [partnerProductId],
      storeNumber,
      config
    );
    const newSkus = {
      ...skuMap,
      sku: partnerProductId.toString(),
    };
    yield put(updateProductInfo({ newSkus, thdDetails }));
    return getPriceData(retail / quantity);
  }
  return null;
}

function* getCurrentTierPricing(
  orderSummaryItems,
  logoCount,
  logoColorCount,
  unitOfProduct,
  previousSelections,
  productName,
  skus,
  isAdd,
  currentItemQuantity,
  siteId,
  productId
) {
  const { attributes } = yield select(productDetailsSelector);

  const {
    uploadImages: { uploadImages },
    config,
  } = yield select();

  const logoLocation = getLogoLocation(uploadImages, attributes?.ppeProductType);
  // All items currently in bundle
  const itemsAlreadyInBundle = orderSummaryItems
    .filter((osItem) => osItem.pricingId)
    .map((osItem) => ({
      logoColors: logoColorCount,
      logos: logoCount,
      partNumber: osItem.sku.sku,
      productName: osItem.name,
      quantity: osItem.quantity,
      uniqueIdentifier: osItem.pricingId,
      unit: unitOfProduct,
      logoLocation
    }));

  const allPayloadData = [...itemsAlreadyInBundle];
  if (isAdd) {

    // Include the item being added to the bundle
    allPayloadData.push({
      ...previousSelections[previousSelections.length - 1],
      productName,
      partNumber: skus[0].sku,
      quantity: currentItemQuantity || 1,
      unit: unitOfProduct,
      logos: logoCount,
      logoColors: logoColorCount,
      logoLocation
    });
  }

  const ppePricePayload = {
    selectedChoices: allPayloadData,
    productId,
    siteId,
    returnCost : false,
  };
  if (ppePricePayload && ppePricePayload.selectedChoices.length > 0) {
    const response = yield call(customLineItemPricing, ppePricePayload);

    return handleMerge(allPayloadData, response);
  }
  return null;
}

function* getFirstTierPricing(
  logoCount,
  logoColorCount,
  unitOfProduct,
  previousSelections,
  productName,
  skus,
  isAdd,
  siteId,
  productId
) {
  const allPayloadData = [];
  if (isAdd) {
    const { attributes } = yield select(productDetailsSelector);

    const {
      uploadImages: { uploadImages },
      config,
    } = yield select();

    const logoLocation = getLogoLocation(uploadImages, attributes?.ppeProductType);

    // Include the item being added to the bundle
    allPayloadData.push({
      ...previousSelections[previousSelections.length - 1],
      productName,
      partNumber: skus[0].sku,
      quantity: 1,
      unit: unitOfProduct,
      logos: logoCount,
      logoColors: logoColorCount,
      logoLocation,
    });
  }

  const ppePricePayload = {
    selectedChoices: allPayloadData,
    productId,
    siteId,
    returnCost : false,
  };
  if (ppePricePayload && ppePricePayload.selectedChoices.length > 0) {
    return yield call(customLineItemPricing, ppePricePayload);
  }
  return null;
}

export function* getPricingForItemsInBundle(isAdd) {
  const { productName } = yield select(configSelector);
  const optionsChoices = {};
  const { options, choices } = yield select(configuratorSelector);
  const optionchoices = yield getSelectedChoices(options, choices);
  const { currentItemQuantity } = yield select(orderSummarySelector);
  const { sizeOfUnit } = yield select(productDetailsAttributeSelector) || '';
  const { previousSelections } = yield select(previousmultipleLineIemSelector) || [];
  const unitOfProduct = parseInt(sizeOfUnit, 10) || 1;
  const { siteId, productId } = yield select(productDetailsSelector);

  for (let i = 0; i < optionchoices.length; i++) {
    const { optionId, shortLabel } = optionchoices[i];
    const customShortLabel = options[optionId].shortLabel.split(' ').join('').toLowerCase();
    optionsChoices[customShortLabel] = shortLabel;
  }

  const {
    uploadImages: { uploadImages },
  } = yield select();
  const { skus } = yield putResolve(getSku());
  let logoCount = uploadImages.filter((img) => !!img.uploadedImageFileName).length;
  logoCount = logoCount > 0 ? logoCount : 1;
  const { orderSummaryItems } = yield select(orderSummarySelector);

  const logoColorCount = yield select(maxColorSelector);
  const tieredPricingData = yield call(
    getCurrentTierPricing,
    orderSummaryItems,
    logoCount,
    logoColorCount,
    unitOfProduct,
    previousSelections,
    productName,
    skus,
    isAdd,
    currentItemQuantity,
    siteId,
    productId
  );

  const strikeThroughPricingData = !isAdd
    ? null
    : yield call(
        getFirstTierPricing,
        logoCount,
        logoColorCount,
        unitOfProduct,
        previousSelections,
        productName,
        skus,
        isAdd,
        siteId,
        productId
      );

  yield putResolve(
    setLineItemPrice({
      tieredPricing: tieredPricingData,
      strikeThroughPricing: strikeThroughPricingData,
    })
  );
}

export default getLineItemPricing;
